import { Space, Typography } from 'antd';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { LinkButton } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input, TextArea } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { BlockSpace } from 'components/atoms/space';
import { useTranslation } from 'hooks/use-translation';
import { Divider, PlaceholderText } from '../commons/styles';
import { ItemLayout } from '../layout';
import { CreateProps as WorkflowProps } from '../use-automation-create';

const { Text, Paragraph } = Typography;

const StyledParagraph = styled(Paragraph)`
  && {
    margin-bottom: 0;
  }
`;

export const WorkflowName = ({
  automationDetails,
  onChange,
}: WorkflowProps): JSX.Element => {
  const name = automationDetails?.automationName ?? '';
  const description = automationDetails?.description ?? '';

  const title = useTranslation(
    'settings.integrations.sf.automation.create.workflow_name.title',
  );

  const [form] = Form.useForm<{ name?: string; description?: string }>();
  const [showModal, setShowModal] = useState(false);

  const Content = () => (
    <BlockSpace direction="vertical" size={16}>
      {name || description ? (
        <Space direction="vertical" size="small">
          <Text strong>{name}</Text>
          {description ? (
            <StyledParagraph>{description}</StyledParagraph>
          ) : null}
        </Space>
      ) : (
        <PlaceholderText>
          <FormattedMessage
            id="settings.integrations.sf.automation.create.workflow_name.placeholder"
            values={{
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        </PlaceholderText>
      )}

      <LinkButton onClick={() => setShowModal(true)} type="link">
        <FormattedMessage id="edit" />
        ...
      </LinkButton>
      <Divider />
    </BlockSpace>
  );

  const handleSubmit = () => {
    form.validateFields().then((val) => {
      onChange({
        ...automationDetails,
        automationName: val.name,
        description: val.description,
      });
      setShowModal(false);
    });
  };

  return (
    <div>
      <ItemLayout right={<Content />} left={title} required />

      <Modal
        onCancel={() => setShowModal(false)}
        onOk={handleSubmit}
        title={title}
        visible={showModal}
        okText={<FormattedMessage id="save" />}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            name,
            description,
          }}
        >
          <Form.Item
            rules={[{ required: true }]}
            name="name"
            label={<FormattedMessage id="name" />}
          >
            <Input autoFocus />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage id="description" />}
            name="description"
          >
            <TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
